import cx from 'classnames';
import {Fragment, useMemo} from 'react';

import {Icon, ParticipantInfo, ParticipantPlatform} from 'components';
import {AnyParticipant, ClientParticipantData, OnlineSession} from 'interfaces';
import {ParticipantRole} from 'types';
import {sessionUtils} from 'utils';

type Props = {
    session: OnlineSession;
    participant: AnyParticipant;
    renderActivityDataIcon?: (session: OnlineSession) => React.ReactNode;
    renderPreSessionFlowBadge?: (session: OnlineSession) => React.ReactNode;
    onOpenPrevClientSessionsModal?: (session: OnlineSession) => void;
};

export const Participant: React.FC<Props> = ({
    participant, session, renderActivityDataIcon, renderPreSessionFlowBadge, onOpenPrevClientSessionsModal
}: Props) => {
    const location = useMemo<string>(() => {
        if (participant?.role === ParticipantRole.Client) {
            const clientData = participant?.data as ClientParticipantData;
            return clientData?.location;
        }
        return null;
    }, [participant]);

    const isLocalParticipant = useMemo(() => sessionUtils.isParticipantLocalUser(participant), [participant]);

    if (!participant) {
        return null;
    }

    return (
        <Fragment>
            <div className='tw-flex tw-items-start'>
                <ParticipantInfo
                    key={participant.id}
                    session={session}
                    spacing={false}
                    participant={participant}
                    local={isLocalParticipant}
                    fields={{clientInfoModal: true, onlineStatus: true}}
                />
                <div className='tw-w-10 tw-ml-1'>
                    <ParticipantPlatform participant={participant} />
                </div>
            </div>
            {participant.role === ParticipantRole.Client && (
                <div className='tw-flex tw-items-center tw-gap-3 tw-ml-7 tw-mt-1 tw-text-sm tw-text-default-dimmed'>
                    {location && <div>{location}</div>}
                    {onOpenPrevClientSessionsModal && session.prevClientSessionsCount > 0 ?
                        <div
                            className='tw-flex tw-items-center tw-cursor-pointer'
                            onClick={() => onOpenPrevClientSessionsModal?.(session)}
                        >
                            <Icon
                                button
                                size='sm'
                                name='rotate'
                                thickness='sm'
                                className='tw-mr-0.5'
                                margin={false}
                            />
                            <span className='tw-text-primary tw-text-nowrap'>
                                {session.prevClientSessionsCount}
                            </span>
                        </div> :
                        null}
                    {renderActivityDataIcon?.(session)}
                    {renderPreSessionFlowBadge?.(session)}
                </div>
            )}
            {participant.role === ParticipantRole.Guest && (
                <div className={cx('tw-flex tw-items-center tw-ml-7 tw-mt-1 tw-text-sm tw-text-default-dimmed')}>
                    <div className='tw-mr-2'>{location}</div>
                    {renderActivityDataIcon?.(session)}
                </div>
            )}
        </Fragment>
    );
};